// src/services/tables/tradeColumns.js - TRADE SCREEN COLUMNS WITH 2-ROW FORMAT
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { formatContractDisplay } from '../contractUtils';
import { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';
import { excludeStaticStatConfigs, STATIC_HITTING_FIELDS, STATIC_PITCHING_FIELDS } from '../../utils/statColumnDedup';

/**
 * Create columns for TRADE SCREEN roster tables WITH 2-ROW FORMAT
 * Shows: Season stats (row 1), 14-Day stats (row 2) - NO ACCRUED
 * 
 * Features:
 * - Narrow columns (28-60px) matching myRosterColumns.js
 * - Clickable rows with highlight for selection
 * - Dynasty theme colors (yellow primary)
 * - Hitters: POS, Name, Team, G, AB, H, [Dynamic Stats], Price, Salary, Contract
 * - Pitchers: POS, Name, Team, G, GS, IP, [Dynamic Stats], Price, Salary, Contract
 * 
 * CANONICAL: player.ids.mlb, player.info.*, player.stats.season.*, player.league_context.financial.*
 */
export const createTradeRosterColumns = ({
  statConfigs = [],
  onPlayerClick,
  selectedPlayers = [], // Array of MLB IDs that are selected
  isPitcherList = false
}) => {
  const columns = [
    // Position Label - Show on first row (season) only
    {
      key: 'position',
      title: 'POS',
      width: 28,
      cellPadding: 'py-0 px-1',
      sortable: false,
      render: (player, rowType, slotInfo) => {
        // Show position on season row only
        if (rowType === 'season' && player) {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.primary} text-xs font-bold uppercase tracking-wide`}>
                {player.info?.position || '?'}
              </span>
            </div>
          );
        }
        return null;
      }
    },
    
    // Player Name with stat type indicator
    {
      key: 'player_name',
      title: 'Player',
      width: 60,
      cellPadding: 'py-0 pl-1 pr-0',
      textAlign: 'text-left',
      sortable: false,
      allowOverflow: true,
      render: (player, rowType, slotInfo) => {
        if (!player) return null;
        // DD-STAT-CONSOLIDATION C3: native totals row label (no player identity)
        if (player.isTotalsRow) {
          return <span className={`${dynastyTheme.classes.text.primary} font-bold text-xs uppercase tracking-wide`}>Totals</span>;
        }

        const isSelected = selectedPlayers.includes(player.ids?.mlb);

        // Season row - show player name
        if (rowType === 'season') {
          return (
            <div className="inline-flex items-center w-full">
              <PlayerLink mlbId={player.ids?.mlb} name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()} style={{ fontWeight: 500, fontSize: '0.875rem', color: isSelected ? '#facc15' : '#f9fafb' }} onClick={(e) => e.stopPropagation()} />
              <InjuryBadge mlbId={player.ids?.mlb} />
            </div>
          );
        } else {
          // 14-Day row - show stat type label
          return (
            <div className="text-left text-[10px] font-normal opacity-50 text-yellow-400">
              ↗ 14-Day
            </div>
          );
        }
      }
    },
    
    // Team column - Show on season row only
    {
      key: 'team',
      title: 'Team',
      width: 32,
      cellPadding: 'py-0 px-1',
      sortable: false,
      render: (player, rowType, slotInfo) => {
        // Show team on season row only
        if (rowType === 'season' && player) {
          return (
            <div className="flex items-center justify-center h-full">
              <span className={`${dynastyTheme.classes.text.white} text-xs`}>
                {player.info?.mlb_team || '-'}
              </span>
            </div>
          );
        }
        return null;
      }
    }
  ];

  // Add initial stat columns based on player type
  if (isPitcherList) {
    // PITCHERS: G, GS, IP
    columns.push(
      {
        key: 'stats.games_played',
        title: 'G',
        width: 32,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          
          let statsData;
          if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
          else if (rowType === 'season') statsData = player.stats?.season;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          // Access nested pitching stats
          const pitchingStats = statsData.pitching || {};
          const value = pitchingStats.games_played;
          
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
        }
      },
      {
        key: 'stats.games_started',
        title: 'GS',
        width: 32,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          
          let statsData;
          if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
          else if (rowType === 'season') statsData = player.stats?.season;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          // Access nested pitching stats
          const pitchingStats = statsData.pitching || {};
          const value = pitchingStats.games_started;
          
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
        }
      },
      {
        key: 'stats.innings_pitched',
        title: 'IP',
        width: 32,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          
          let statsData;
          if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
          else if (rowType === 'season') statsData = player.stats?.season;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          // Access nested pitching stats
          const pitchingStats = statsData.pitching || {};
          const value = pitchingStats.innings_pitched;
          
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const formattedValue = renderFloat1(value);
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formattedValue}</span>;
        }
      }
    );
  } else {
    // BATTERS: G, AB, H
    columns.push(
      {
        key: 'stats.games_played',
        title: 'G',
        width: 32,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          
          let statsData;
          if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
          else if (rowType === 'season') statsData = player.stats?.season;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          // Access nested hitting stats
          const hittingStats = statsData.hitting || {};
          const value = hittingStats.games_played;
          
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
        }
      },
      {
        key: 'stats.at_bats',
        title: 'AB',
        width: 32,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          
          let statsData;
          if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
          else if (rowType === 'season') statsData = player.stats?.season;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          // Access nested hitting stats
          const hittingStats = statsData.hitting || {};
          const value = hittingStats.at_bats;
          
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
        }
      },
      {
        key: 'stats.hits',
        title: 'H',
        width: 32,
        cellPadding: 'py-0 px-1',
        sortable: false,
        isStatColumn: true,
        render: (player, rowType, slotInfo) => {
          if (!player) return null;
          
          let statsData;
          if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
          else if (rowType === 'season') statsData = player.stats?.season;
          else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;
          
          if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          
          // Access nested hitting stats
          const hittingStats = statsData.hitting || {};
          const value = hittingStats.hits;
          
          if (value === undefined || value === null) {
            return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
          }
          
          const colorClass = rowType === 'season' 
            ? dynastyTheme.classes.text.white
            : 'text-yellow-400';
          const fontClass = rowType === 'season' ? '' : 'text-xs italic';
          
          return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{value}</span>;
        }
      }
    );
  }

  // Add dynamic stat columns based on league settings.
  // DD-STAT-CONSOLIDATION sweep: drop any that duplicate the static context block
  // above (hitting G/AB/H, pitching G/GS/IP) — e.g. a league that scores IP.
  excludeStaticStatConfigs(
    statConfigs,
    isPitcherList ? STATIC_PITCHING_FIELDS : STATIC_HITTING_FIELDS
  ).forEach((config) => {
    columns.push({
      key: `stats.${config.field}`,
      title: config.label,
      width: 32,
      cellPadding: 'py-0 px-1',
      sortable: false,
      isStatColumn: true,
      render: (player, rowType, slotInfo) => {
        if (!player) return null;
        
        let statsData;
        if (player?.isTotalsRow) statsData = player.__stats; // DD-STAT-CONSOLIDATION C3: native totals row
        else if (rowType === 'season') statsData = player.stats?.season;
        else if (rowType === 'rolling') statsData = player.stats?.rolling_14_day;

        if (!statsData) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;

        // Access nested stats - try hitting first, then pitching
        const hittingStats = statsData.hitting || {};
        const pitchingStats = statsData.pitching || {};
        const value = hittingStats[config.field] !== undefined ? hittingStats[config.field] : pitchingStats[config.field];
        
        if (value === undefined || value === null) {
          return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;
        }
        
        // Format based on config type
        let formattedValue;
        if (config.format === 'avg') {
          formattedValue = renderAvg(value);
        } else if (config.format === 'float2') {
          formattedValue = renderFloat2(value);
        } else if (config.format === 'float3') {
          formattedValue = renderFloat3(value);
        } else {
          formattedValue = renderDefault(value);
        }
        
        // Color based on row type (white for season, yellow for rolling)
        const colorClass = rowType === 'season' 
          ? dynastyTheme.classes.text.white
          : 'text-yellow-400';
        
        // Font size: normal for season, smaller+italic for rolling
        const fontClass = rowType === 'season' ? '' : 'text-xs italic';
        
        return <span className={`${colorClass} ${fontClass} font-mono text-sm`}>{formattedValue}</span>;
      }
    });
  });

  // Price column - Show on SEASON row only
  columns.push({
    key: 'price',
    title: 'Price',
    width: 42,
    cellPadding: 'py-0 px-1',
    sortable: false,
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'season') return null;
      if (!player) return null;
      
      const price = player.league_context?.financial?.baseline_price;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${price != null ? dynastyTheme.classes.text.warning : dynastyTheme.classes.text.neutralLight} font-semibold text-sm`}>
            {price != null ? `$${price}` : '\u2014'}
          </span>
        </div>
      );
    }
  });

  // Salary column - Show on SEASON row only
  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 42,
    cellPadding: 'py-0 px-1',
    sortable: false,
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'season') return null;
      if (!player) return null;
      
      const salary = player.league_context?.financial?.contract_salary;
      const hasContract = salary != null && salary > 0;

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${hasContract ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.neutralLight} font-semibold text-sm`}>
            {hasContract ? `$${salary}` : '\u2014'}
          </span>
        </div>
      );
    }
  });

  // Contract column - Show on SEASON row only
  columns.push({
    key: 'contract',
    title: 'Contract',
    width: 52,
    cellPadding: 'py-0 px-1',
    sortable: false,
    render: (player, rowType, slotInfo) => {
      if (rowType !== 'season') return null;
      if (!player) return null;
      
      const salary = player.league_context?.financial?.contract_salary;
      const hasContract = salary != null && salary > 0;
      const display = hasContract
        ? formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years)
        : '\u2014';

      return (
        <div className="flex items-center justify-center h-full">
          <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralLight : `${dynastyTheme.classes.text.white} font-semibold`} text-sm`}>
            {display}
          </span>
        </div>
      );
    }
  });

  return columns;
};


